import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Skeleton } from '@/common/ui/skeleton'
import { DollarSign, TrendingUp, Calculator, Hash } from 'lucide-react'

import type { TrueMoneyDepositsResponse } from '../../../types'

interface TrueMoneyDepositsSummaryCardProps {
	data?: TrueMoneyDepositsResponse
	isLoading?: boolean
}

export default function TrueMoneyDepositsSummaryCard({
	data,
	isLoading = false,
}: TrueMoneyDepositsSummaryCardProps) {
	const summaryCards = [
		{
			title: 'ยอดฝากรวม',
			value: data ? `฿${data.totalAmount.toLocaleString()}` : '฿0',
			icon: DollarSign,
			color: 'text-green-600',
			bgColor: 'bg-green-50 dark:bg-green-950',
		},
		{
			title: 'จำนวนธุรกรรม',
			value: data ? data.totalCount.toLocaleString() : '0',
			subValue: 'ครั้ง',
			icon: Hash,
			color: 'text-blue-600',
			bgColor: 'bg-blue-50 dark:bg-blue-950',
		},

		{
			title: 'ยอดเฉลี่ย',
			value: data ? `฿${data.averageAmount.toLocaleString()}` : '฿0',
			subValue: 'ต่อธุรกรรม',
			icon: TrendingUp,
			color: 'text-purple-600',
			bgColor: 'bg-purple-50 dark:bg-purple-950',
		},
		{
			title: 'ค่าธรรมเนียมรวม',
			value: data ? `฿${data.totalFeeAmount.toLocaleString()}` : '฿0',
			icon: Calculator,
			color: 'text-orange-600',
			bgColor: 'bg-orange-50 dark:bg-orange-950',
		},
	]

	if (isLoading) {
		return (
			<Card className="mb-6">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Skeleton className="h-5 w-5 rounded-full" />
						<Skeleton className="h-5 w-40" />
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
						{Array.from({ length: 4 }).map((_, index) => (
							<div key={index} className="space-y-2">
								<Skeleton className="h-4 w-24" />
								<Skeleton className="h-8 w-full" />
								<Skeleton className="h-4 w-16" />
							</div>
						))}
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card className="mb-6 border-2 border-blue-200 bg-blue-50/30 dark:border-blue-800 dark:bg-blue-950/30">
			<CardHeader>
				<CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
					<DollarSign className="h-5 w-5" />
					สรุปยอดฝาก TrueMoney
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
					{summaryCards.map((card, index) => {
						const Icon = card.icon
						return (
							<div
								key={index}
								className="flex items-center gap-3 rounded-lg border bg-white p-4 shadow-sm transition-all hover:shadow-md dark:bg-gray-900"
							>
								<div className={`rounded-full p-2 ${card.bgColor}`}>
									<Icon className={`h-5 w-5 ${card.color}`} />
								</div>
								<div className="flex-1">
									<p className="text-sm text-muted-foreground">{card.title}</p>
									<div className={`text-lg font-bold ${card.color}`}>
										{card.value}
									</div>
									{card.subValue && (
										<p className="text-xs text-muted-foreground">
											{card.subValue}
										</p>
									)}
								</div>
							</div>
						)
					})}
				</div>
			</CardContent>
		</Card>
	)
}
